import React, { createContext, useContext, useEffect, useReducer, useCallback } from 'react';
import { auth as authApi } from '../api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

const initialState = {
  user: null,
  loading: true,
  isAuthenticated: false,
};

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_USER': return { ...state, user: action.payload, isAuthenticated: !!action.payload, loading: false };
    case 'SET_LOADING': return { ...state, loading: action.payload };
    case 'LOGOUT': return { ...initialState, loading: false };
    default: return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) { dispatch({ type: 'SET_USER', payload: null }); return; }
    try {
      const res = await authApi.getProfile();
      dispatch({ type: 'SET_USER', payload: res.data.data });
    } catch {
      localStorage.clear();
      dispatch({ type: 'SET_USER', payload: null });
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login({ email, password });
    const { user, accessToken, refreshToken } = res.data.data;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    dispatch({ type: 'SET_USER', payload: user });
    toast.success(`Welcome back, ${user.first_name}!`);
    return user;
  }, []);

  const register = useCallback(async (data) => {
    const res = await authApi.register(data);
    toast.success('Registration successful! Please verify your email.');
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch {}
    localStorage.clear();
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  }, []);

  const updateUser = useCallback((data) => {
    dispatch({ type: 'SET_USER', payload: { ...state.user, ...data } });
  }, [state.user]);

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, updateUser, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
